import type { AxisSelection } from '@atlas/types'
import type { FilterAxis } from '@atlas/ui-components'
import { AddAffordance, AxisMultiSelect, CardShell } from '@atlas/ui-components'
import { Tag } from '@atlas/ui-primitives'
import { useState } from 'react'
import { CAMPAIGN_STATUS_LABELS, CampaignStatus } from '@/domain/campaign.types'
import type {
	CampaignClassificationOption,
	CampaignClassificationVariable,
} from '@/domain/campaign-classification-catalog'

export interface CampaignFilterRailProps {
	statusSelection: readonly CampaignStatus[]
	onToggleStatus: (status: CampaignStatus) => void

	objective?: CampaignClassificationVariable
	geography?: CampaignClassificationVariable
	shownVariables: readonly CampaignClassificationVariable[]
	addableVariables: readonly CampaignClassificationVariable[]
	classificationSelection: AxisSelection
	onToggleClassification: (axisCode: string, valueCode: string) => void
	onAddAxis: (axisCode: string) => void
	onRemoveAxis: (axisCode: string) => void
}

function toFilterAxis(variable: CampaignClassificationVariable): FilterAxis {
	return {
		code: variable.axisCode,
		label: variable.label,
		options: variable.options.map((option) => ({
			code: option.code,
			label: option.label,
			depth: option.parentCode ? 1 : 0,
		})),
	}
}

/**
 * A value's visible label, prefixed with its parent's label when the supplied
 * options carry that parent. Resolved one level only, from the catalog data the
 * rail was given — the rail never walks a closure, never resolves ancestry
 * recursively, and never supplies a label the catalog did not.
 */
function optionLabel(options: readonly CampaignClassificationOption[], option: CampaignClassificationOption): string {
	if (!option.parentCode) return option.label
	const parent = options.find((candidate) => candidate.code === option.parentCode)
	return parent === undefined ? option.label : `${parent.label} · ${option.label}`
}

const CHIP_BASE =
	'rounded-ds-full border px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border'
const CHIP_SELECTED = 'border-ds-primary bg-ds-primary text-ds-primary-foreground'
const CHIP_UNSELECTED =
	'border-ds-border bg-ds-surface-elevated text-ds-text-muted hover:border-ds-text-subtle hover:text-ds-text'

/**
 * One filter value. A native button carrying `aria-pressed`, so its selected
 * state is exposed programmatically rather than by styling alone and Enter and
 * Space activate it through native button behaviour — no role, no tabIndex, and
 * no synthetic key handling.
 */
function FilterChip({ label, selected, onToggle }: { label: string; selected: boolean; onToggle: () => void }) {
	return (
		<button
			type="button"
			aria-pressed={selected}
			onClick={onToggle}
			className={`${CHIP_BASE} ${selected ? CHIP_SELECTED : CHIP_UNSELECTED}`}
		>
			{label}
		</button>
	)
}

/** One fixed dimension: a native `<fieldset>`/`<legend>` group whose values wrap rather than overflow. */
function FilterChipGroup({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<fieldset className="space-y-2">
			<legend className="text-sm font-semibold text-ds-text">{label}</legend>
			<div className="flex flex-wrap gap-2">{children}</div>
		</fieldset>
	)
}

/**
 * The Campaign list's Filter Rail. One containing panel holds the three fixed
 * groups — Status, Objective, Geography — each as a labelled group of inline
 * value controls, followed by any dynamically-added Classification group and
 * the "+ Add variable" affordance that reveals one, all inside that same
 * panel. Status remains a non-Classification filter from the `CampaignStatus`
 * enum and its label map; Objective and Geography values, labels, and ancestry
 * come only from the supplied Classification catalog.
 *
 * Dynamically-added Classification variables keep their existing
 * `AxisMultiSelect` presentation and add/remove behaviour — only the shared
 * `FilterRail`'s own outer card is not reused here, so the rail reads as one
 * surface rather than two. All data and callbacks arrive as props: the rail
 * holds no selection state and performs no filtering.
 */
export function CampaignFilterRail({
	statusSelection,
	onToggleStatus,
	objective,
	geography,
	shownVariables,
	addableVariables,
	classificationSelection,
	onToggleClassification,
	onAddAxis,
	onRemoveAxis,
}: CampaignFilterRailProps) {
	const [adding, setAdding] = useState(false)

	// The fixed Objective and Geography groups are rendered directly here, so the
	// dynamic axes below carry only the addable/added Classification variables.
	const dynamicVariables = [...shownVariables, ...addableVariables]
	const dynamicAxes = dynamicVariables.map(toFilterAxis)
	const shownAxisCodes = shownVariables.map((variable) => variable.axisCode)
	const shownDynamicAxes = dynamicAxes.filter((axis) => shownAxisCodes.includes(axis.code))
	const hiddenDynamicAxes = dynamicAxes.filter((axis) => !shownAxisCodes.includes(axis.code))

	return (
		<CardShell className="space-y-5">
			<p className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">Filter rail</p>

			<FilterChipGroup label="Status">
				{Object.values(CampaignStatus).map((status) => (
					<FilterChip
						key={status}
						label={CAMPAIGN_STATUS_LABELS[status]}
						selected={statusSelection.includes(status)}
						onToggle={() => onToggleStatus(status)}
					/>
				))}
			</FilterChipGroup>

			{/* A fixed group keeps its dimension label and renders no value until the
			    catalog supplies one — an unresolved or value-less axis is not an error. */}
			<FilterChipGroup label={objective?.label ?? 'Objective'}>
				{(objective?.options ?? []).map((option) => (
					<FilterChip
						key={option.code}
						label={optionLabel(objective?.options ?? [], option)}
						selected={(classificationSelection[objective?.axisCode ?? ''] ?? []).includes(option.code)}
						onToggle={() => objective && onToggleClassification(objective.axisCode, option.code)}
					/>
				))}
			</FilterChipGroup>

			<FilterChipGroup label={geography?.label ?? 'Geography'}>
				{(geography?.options ?? []).map((option) => (
					<FilterChip
						key={option.code}
						label={optionLabel(geography?.options ?? [], option)}
						selected={(classificationSelection[geography?.axisCode ?? ''] ?? []).includes(option.code)}
						onToggle={() => geography && onToggleClassification(geography.axisCode, option.code)}
					/>
				))}
			</FilterChipGroup>

			{shownDynamicAxes.map((axis) => (
				<AxisMultiSelect
					key={axis.code}
					axis={axis}
					selectedCodes={classificationSelection[axis.code] ?? []}
					onToggle={(valueCode) => onToggleClassification(axis.code, valueCode)}
					onRemove={() => onRemoveAxis(axis.code)}
				/>
			))}

			{hiddenDynamicAxes.length > 0 && (
				<div className="border-t border-dashed border-ds-border pt-4">
					<AddAffordance
						block
						label="Add variable"
						hint="dimension or attribute"
						onPress={() => setAdding((v) => !v)}
					/>
					{adding && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{hiddenDynamicAxes.map((axis) => (
								<button
									key={axis.code}
									type="button"
									onClick={() => {
										onAddAxis(axis.code)
										setAdding(false)
									}}
								>
									<Tag variant="primary" size="sm">
										{axis.label}
									</Tag>
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</CardShell>
	)
}
